import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Pagination from '../components/Pagination';
import { 
  PlusCircleIcon, 
  CheckCircleIcon, 
  XCircleIcon, 
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentIcon,
  DocumentPlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import ReceiptForm from '../components/ReceiptForm';
import { useMsal } from '@azure/msal-react';
import api from '../api';
import AddReceiptForm from '../components/AddReceiptForm';

const GoodsReceiptsPage = ({ userRole: propUserRole }) => {
  const [rows, setRows] = useState([]); // flat rows from expense_goods_receipts
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expenseType, setExpenseType] = useState('all');
  const [userRole, setUserRole] = useState(propUserRole);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showReceiptForm, setShowReceiptForm] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'expense_id', direction: 'desc' });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10, // Default to 10 entries per page
    totalItems: 0
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [updatingExpenseId, setUpdatingExpenseId] = useState(null);
  
  const { instance } = useMsal();
  const navigate = useNavigate();

  // Update userRole when prop changes
  useEffect(() => {
    if (propUserRole) {
      setUserRole(propUserRole);
    }
  }, [propUserRole]);



  const fetchGoodsReceipts = async () => {
    try {
      setLoading(true);
      
      // Fetch all expenses and filter for those that can receive goods
      const allExpensesResponse = await api.get('/api/expenses');
      const allExpenses = Array.isArray(allExpensesResponse.data) ? allExpensesResponse.data : [];
      
      // Filter for expenses that are approved or payment done (goods can be received in both states)
      const eligibleStatuses = ['Approved', 'Payment Done', 'Payment Approved'];
      const rawApprovedExpenses = allExpenses.filter(expense => 
        eligibleStatuses.includes(expense.status)
      );
      
      // Deduplicate expenses by expense_id to avoid showing the same expense multiple times
      const expenseMap = new Map();
      rawApprovedExpenses.forEach(expense => {
        const expenseId = expense.expense_id || expense.id;
        if (!expenseMap.has(expenseId)) {
          expenseMap.set(expenseId, expense);
        }
      });
      const approvedExpenses = Array.from(expenseMap.values());
      
      if (approvedExpenses.length === 0) {
        setRows([]);
        setFilteredRows([]);
        setPagination(prev => ({ ...prev, totalItems: 0, currentPage: 1 }));
        return;
      }
      
      // PERFORMANCE OPTIMIZATION: Process all expenses but with better batching
      const batchSize = 8; // Optimal batch size for performance
      const batches = [];
      for (let i = 0; i < approvedExpenses.length; i += batchSize) {
        batches.push(approvedExpenses.slice(i, i + batchSize));
      }
      
      const allResults = [];
      
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        
        // Use Promise.allSettled for better error handling
        const batchResults = await Promise.allSettled(
          batch.map(async (expense) => {
            try {
              const expId = expense.expense_id || expense.id;
              
              // PERFORMANCE OPTIMIZATION: Combine API calls with Promise.all
              // Use query params (?expenseId=) instead of path params to avoid Azure slash pre-decoding issue
              const [orderedItemsResp, receiptsResp] = await Promise.all([
                api.get(`/api/goods-receipts/ordered-items`, { params: { expenseId: expId } }).catch(() => ({ data: [] })),
                api.get(`/api/goods-receipts/receipts-by`, { params: { expenseId: expId } }).catch(() => ({ data: [] }))
              ]);
              
              const orderedItems = Array.isArray(orderedItemsResp.data) ? orderedItemsResp.data : [];
              const receipts = Array.isArray(receiptsResp.data) ? receiptsResp.data : [];
              
              // Debug logging for the specific expense we're tracking
              if (expense.expense_id === 'AUC/ADMIN/2026/1017') {
                console.log(`🔍 Raw data for ${expense.expense_id}:`, {
                  orderedItems: orderedItems,
                  receipts: receipts,
                  orderedItemsStructure: orderedItems.map(item => ({
                    item_id: item.item_id,
                    expense_line_id: item.expense_line_id,
                    display_name: item.display_name || item.item_name,
                    type: item.type
                  })),
                  receiptsStructure: receipts.map(receipt => ({
                    expense_item_id: receipt.expense_item_id,
                    expense_line_id: receipt.expense_line_id,
                    received_qty: receipt.received_qty
                  }))
                });
              }
              
              if (orderedItems.length === 0) {
                return null; // Skip expenses with no ordered items
              }
              
              // Create aggregated data combining ordered items with receipt data
              const orderedQuantities = orderedItems.map(item => item.ordered_qty || 0);
              
              // Calculate received quantities from actual receipts data - PER ITEM
              const receivedQuantities = orderedItems.map(item => {
                // Find receipts specifically for THIS item only
                const itemReceipts = receipts.filter(receipt => {
                  // For inventory items: match by expense_item_id to item_id
                  if (item.item_id && receipt.expense_item_id) {
                    return receipt.expense_item_id.toString() === item.item_id.toString();
                  }
                  
                  // For non-inventory items: try multiple matching strategies
                  if (!item.item_id) { // Non-inventory item
                    // Strategy 1: Match by expense_line_id (new receipts)
                    if (item.expense_line_id && receipt.expense_line_id) {
                      return receipt.expense_line_id.toString() === item.expense_line_id.toString();
                    }
                    
                    // Strategy 2: Match by expense_item_id to expense_line_id (legacy receipts)
                    // For existing receipts where expense_item_id stores the expense_line_id value
                    if (item.expense_line_id && receipt.expense_item_id) {
                      return receipt.expense_item_id.toString() === item.expense_line_id.toString();
                    }
                    
                    // Strategy 3: Match by expense_item_id to item.id (fallback)
                    if (item.id && receipt.expense_item_id) {
                      return receipt.expense_item_id.toString() === item.id.toString();
                    }
                  }
                  
                  return false;
                });
                
                // Sum up received quantities for THIS SPECIFIC ITEM only
                const totalReceivedForThisItem = itemReceipts.reduce((sum, receipt) => {
                  const qty = parseFloat(receipt.received_qty) || 0;
                  return sum + qty;
                }, 0);
                
                // Debug logging for the specific expense we're tracking
                if (expense.expense_id === 'AUC/ADMIN/2026/1003' || expense.expense_id === 'AUC/ADMIN/2026/1017') {
                  console.log(`📊 Item-specific calculation for ${item.display_name || item.item_name || item.free_text_name}:`, {
                    itemId: item.item_id,
                    itemExpenseLineId: item.expense_line_id,
                    itemIdField: item.id,
                    matchingReceipts: itemReceipts,
                    receivedQtyForThisItem: totalReceivedForThisItem,
                    receiptDetails: itemReceipts.map(r => ({
                      expense_item_id: r.expense_item_id,
                      expense_line_id: r.expense_line_id,
                      received_qty: r.received_qty
                    })),
                    allReceipts: receipts.map(r => ({
                      expense_item_id: r.expense_item_id,
                      expense_line_id: r.expense_line_id,
                      received_qty: r.received_qty
                    }))
                  });
                }
                
                return totalReceivedForThisItem;
              });
              
              const receiptFiles = receipts.map(r => r.receipt_file).filter(f => f);
              
              return {
                id: receipts.length > 0 ? receipts[0].id : `placeholder-${expense.expense_id || expense.id}`,
                expense_id: expense.expense_id || expense.id,
                expense_name: expense.expense_name || '',
                item_names: orderedItems.map(i => i.display_name || i.item_name || i.free_text_name || '—'),
                skus: orderedItems.map(i => i.sku || '—'),
                uoms: orderedItems.map(i => i.uom || '—'),
                ordered_quantities: orderedQuantities,
                quantities: receivedQuantities,
                notes_list: receipts.map(r => r.notes || '—'),
                receipt_files: receiptFiles,
                created_at: receipts.length > 0 ? receipts[0].created_at : null,
                expense_reference: expense.expense_id || `EXP-${expense.id}`,
                isPlaceholder: receipts.length === 0,
                receipts_closed: Number(expense.receipts_closed) === 1,
              };
            } catch (e) {
              console.warn(`Failed to process expense ${expense.expense_id}:`, e.message);
              return null;
            }
          })
        );
        
        // Filter successful results and add to all results
        const successfulResults = batchResults
          .filter(result => result.status === 'fulfilled' && result.value !== null)
          .map(result => result.value);
        
        allResults.push(...successfulResults);
        
        // PROGRESSIVE LOADING: Update UI with data as it loads
        if (successfulResults.length > 0) {
          setRows([...allResults]);
          setFilteredRows([...allResults]);
          setPagination(prev => ({ ...prev, totalItems: allResults.length }));
        }
        
        // Update progress with both percentage and count
        const progressPercent = Math.round(((batchIndex + 1) / batches.length) * 100);
        setLoadingProgress(progressPercent);
        
        // PERFORMANCE OPTIMIZATION: Minimal delay between batches for responsiveness
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 25)); // Reduced to 25ms for faster processing
        }
      }
      
      setRows(allResults);
      setFilteredRows(allResults);
      setPagination(prev => ({ ...prev, totalItems: allResults.length, currentPage: 1 }));
      setLastRefresh(new Date());
      
    } catch (error) {
      console.error('Error fetching goods receipts:', error);
      setRows([]);
      setFilteredRows([]);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
      setLoadingProgress(0);
    }
  };



  // Handle search and filtering with debouncing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      const term = searchTerm.toLowerCase();
      const asText = (v) => Array.isArray(v) ? v.join(', ').toLowerCase() : ((v || '').toString().toLowerCase());
      
      let filtered = rows.filter(r =>
        asText(r.expense_id).includes(term) ||
        asText(r.expense_name).includes(term) ||
        asText(r.expense_reference).includes(term) ||
        asText(r.item_name ?? r.item_names).includes(term) ||
        asText(r.sku ?? r.skus).includes(term) ||
        asText(r.uom ?? r.uoms).includes(term) ||
        asText(r.notes ?? r.notes_list).includes(term)
      );

      // Apply expense type filter
      if (expenseType !== 'all') {
        filtered = filtered.filter(r => {
          if (!r.expense_id) return false;
          const id = r.expense_id.toUpperCase();
          if (expenseType === 'IT') return id.includes('/IT/');
          if (expenseType === 'Accounts') return id.includes('/ACCOUNT/');
          if (expenseType === 'Admin') return !id.includes('/IT/') && !id.includes('/ACCOUNT/');
          return true;
        });
      }

      setFilteredRows(filtered);
      setPagination(prev => ({ ...prev, totalItems: filtered.length, currentPage: 1 }));
    }, 300); // 300ms debounce

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, expenseType, rows]);

  // Handle sorting
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Handle pagination
  const handlePageChange = (pageNumber) => {
    setPagination(prev => ({
      ...prev,
      currentPage: pageNumber
    }));
  };

  // Apply sorting and pagination
  const sortedAndPaginatedRows = useMemo(() => {
    const sortableItems = [...filteredRows];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    return sortableItems.slice(startIndex, startIndex + pagination.itemsPerPage);
  }, [filteredRows, sortConfig, pagination.currentPage, pagination.itemsPerPage]);
  
  // Calculate pagination
  const totalPages = Math.ceil(pagination.totalItems / pagination.itemsPerPage);
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Handle receipt save
  const handleAddReceipt = (expense) => {
    setSelectedExpense(expense);
    setShowReceiptForm(true);
  };

  const handleReceiptSaved = (updatedExpenseId) => {
    setShowReceiptForm(false);
    setSelectedExpense(null);
    
    // Update the local data to reflect the new received quantities
    if (updatedExpenseId) {
      updateLocalReceiptData(updatedExpenseId);
    }
    
    // Show success message
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  // Function to update local data for a specific expense
  const updateLocalReceiptData = async (expenseId) => {
    try {
      setUpdatingExpenseId(expenseId); // Show updating indicator
      const encodedExpenseId = encodeURIComponent(expenseId);
      
      // Use query params to avoid Azure slash pre-decoding issue
      const [orderedItemsResp, receiptsResp] = await Promise.all([
        api.get(`/api/goods-receipts/ordered-items`, { params: { expenseId } }).catch(() => ({ data: [] })),
        api.get(`/api/goods-receipts/receipts-by`, { params: { expenseId } }).catch(() => ({ data: [] }))
      ]);
      
      const orderedItems = Array.isArray(orderedItemsResp.data) ? orderedItemsResp.data : [];
      const receipts = Array.isArray(receiptsResp.data) ? receiptsResp.data : [];
      
      if (orderedItems.length > 0) {
        // Calculate received quantities from actual receipts data - PER ITEM
        const receivedQuantities = orderedItems.map(item => {
          // Find receipts specifically for THIS item only
          const itemReceipts = receipts.filter(receipt => {
            // For inventory items: match by expense_item_id to item_id
            if (item.item_id && receipt.expense_item_id) {
              return receipt.expense_item_id.toString() === item.item_id.toString();
            }
            
            // For non-inventory items: try multiple matching strategies
            if (!item.item_id) { // Non-inventory item
              // Strategy 1: Match by expense_line_id (new receipts)
              if (item.expense_line_id && receipt.expense_line_id) {
                return receipt.expense_line_id.toString() === item.expense_line_id.toString();
              }
              
              // Strategy 2: Match by expense_item_id to expense_line_id (legacy receipts)
              if (item.expense_line_id && receipt.expense_item_id) {
                return receipt.expense_item_id.toString() === item.expense_line_id.toString();
              }
              
              // Strategy 3: Match by expense_item_id to item.id (fallback)
              if (item.id && receipt.expense_item_id) {
                return receipt.expense_item_id.toString() === item.id.toString();
              }
            }
            
            return false;
          });
          
          // Sum up received quantities for THIS SPECIFIC ITEM only
          const totalReceivedForThisItem = itemReceipts.reduce((sum, receipt) => {
            const qty = parseFloat(receipt.received_qty) || 0;
            return sum + qty;
          }, 0);
          
          return totalReceivedForThisItem;
        });
        
        const receiptFiles = receipts.map(r => r.receipt_file).filter(f => f);
        
        // Update the rows with new data
        setRows(prevRows => {
          return prevRows.map(row => {
            if (row.expense_id === expenseId) {
              return {
                ...row,
                quantities: receivedQuantities,
                receipt_files: receiptFiles,
                notes_list: receipts.map(r => r.notes || '—'),
                isPlaceholder: receipts.length === 0,
              };
            }
            return row;
          });
        });
        
        // Also update filtered rows
        setFilteredRows(prevRows => {
          return prevRows.map(row => {
            if (row.expense_id === expenseId) {
              return {
                ...row,
                quantities: receivedQuantities,
                receipt_files: receiptFiles,
                notes_list: receipts.map(r => r.notes || '—'),
                isPlaceholder: receipts.length === 0,
              };
            }
            return row;
          });
        });
      }
    } catch (error) {
      console.error('Error updating local receipt data:', error);
    } finally {
      setUpdatingExpenseId(null); // Hide updating indicator
    }
  };

  const columns = [
    { key: 'expense_id', label: 'Expense ID', sortable: true, className: 'font-medium w-32' },
    { key: 'expense_name', label: 'Expense Name', sortable: true, className: 'max-w-xs' },
    { key: 'ordered_quantities', label: 'Ordered', sortable: true, className: 'text-center w-20' },
    { key: 'quantities', label: 'Received', sortable: true, className: 'text-center w-20' },
    { key: 'receipt_files', label: 'Files', sortable: false, className: 'text-center w-16' },
    { key: 'actions', label: 'Actions', sortable: false, className: 'text-center w-32' },
  ];

  const renderCell = (row, column) => {
    const value = row[column.key];
    
    if (column.key === 'actions') {
      const closed = Boolean(row.receipts_closed);
      return (
        <div className="flex justify-center">
          <button
            onClick={() => { if (!closed) handleAddReceipt(row); }}
            className={`px-2.5 py-1.5 rounded-md transition flex items-center text-xs font-semibold ${closed ? 'cursor-not-allowed opacity-60' : ''}`}
            style={closed 
              ? { background: 'rgba(148,163,184,0.16)', color: 'var(--muted)' }
              : { background: '#8c211e', color: '#fff' }
            }
            title={closed ? 'Receipts closed for this expense' : 'Add Receipt'}
            disabled={closed}
          >
            <DocumentPlusIcon className="h-3.5 w-3.5 mr-1" />
            Add
          </button>
        </div>
      );
    }
    
    if (column.key === 'receipt_files') {
      const files = Array.isArray(value) ? value : [];
      return (
        <div className="flex justify-center">
          {files.length > 0 && files.some(f => f) ? (
            <div className="flex flex-col space-y-1">
              {files.filter(f => f).slice(0, 3).map((file, idx) => (
                <a
                  key={idx}
                  href={file.startsWith('http') ? file : `/${file}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium transition hover:opacity-80"
                  style={{ background: 'var(--primary)', color: '#fff' }}
                  onClick={(e) => e.stopPropagation()}
                  title="View Receipt File"
                >
                  <DocumentIcon className="h-3 w-3 mr-1" />
                  View
                </a>
              ))}
              {files.filter(f => f).length > 3 && (
                <span className="text-xs text-center" style={{ color: 'var(--muted)' }}>
                  +{files.filter(f => f).length - 3} more
                </span>
              )}
            </div>
          ) : (
            <span className="text-xs" style={{ color: 'var(--muted)' }}>—</span>
          )}
        </div>
      );
    }
    
    if (column.key === 'expense_name') {
      return (
        <div className="max-w-xs">
          <div className="truncate" title={value}>{value || '—'}</div>
        </div>
      );
    }
    
    if ((column.key === 'quantities' || column.key === 'ordered_quantities') && Array.isArray(value)) {
      const totalQty = value.reduce((sum, qty) => {
        const num = parseFloat(qty);
        return sum + (isNaN(num) ? 0 : num);
      }, 0);
      
      return (
        <div className="text-center">
          <div className="font-medium">{totalQty}</div>
        </div>
      );
    }
    
    return value || '—';
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if user is authenticated
        const account = instance.getActiveAccount();
        if (!account) {
          navigate('/');
          return;
        }

        // Get user role
        const response = await api.get(`/api/users/${account.username}`);
        const role = response.data.role;
        setUserRole(role);
        // Fetch data for all roles but UI will limit actions for non-admin/creator
        fetchGoodsReceipts(); // Load data
      } catch (error) {
        console.error('Error checking auth:', error);
        navigate('/');
      }
    };

    // Set up global callback for updating receipt data
    window.updateGoodsReceiptData = updateLocalReceiptData;

    checkAuth();

    // Cleanup
    return () => {
      delete window.updateGoodsReceiptData;
    };
  }, [instance, navigate]);

  if (loading && isInitialLoad) {
    return (
      <div className="p-6 w-full space-y-6" style={{ color: 'var(--text)' }}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Goods Receipts</h1>
            <p className="text-sm" style={{ color: 'var(--muted)' }}>Track and manage received goods against purchase orders</p>
          </div>
        </div>

        <div className="rounded-xl shadow-sm p-6 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
          <div className="animate-pulse">
            <div className="flex items-center justify-between mb-4">
              <div className="h-4 rounded w-1/4" style={{ background: 'var(--border)' }}></div>
              <div className="text-sm" style={{ color: 'var(--muted)' }}>
                Loading all available data... {loadingProgress}%
              </div>
            </div>
            {loadingProgress > 0 && (
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div 
                  className="h-2 rounded-full transition-all duration-300" 
                  style={{ 
                    background: 'linear-gradient(90deg, #5c1417 0%, #801c23 100%)', 
                    width: `${loadingProgress}%` 
                  }}
                ></div>
              </div>
            )}
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex space-x-4">
                  <div className="h-4 rounded w-1/6" style={{ background: 'var(--border)' }}></div>
                  <div className="h-4 rounded w-1/4" style={{ background: 'var(--border)' }}></div>
                  <div className="h-4 rounded w-1/8" style={{ background: 'var(--border)' }}></div>
                  <div className="h-4 rounded w-1/8" style={{ background: 'var(--border)' }}></div>
                  <div className="h-4 rounded w-1/6" style={{ background: 'var(--border)' }}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Allow all roles to view; actions will be limited below

  // Render sort icon
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <span className="ml-1 opacity-0 group-hover:opacity-100">↕️</span>;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon className="ml-1 h-4 w-4 text-blue-500" />
    ) : (
      <ChevronDownIcon className="ml-1 h-4 w-4 text-blue-500" />
    );
  };

  return (
    <div className="p-6 w-full space-y-6" style={{ color: 'var(--text)' }}>
      {showReceiptForm && selectedExpense && (
        <ReceiptForm 
          expense={selectedExpense}
          onClose={() => setShowReceiptForm(false)}
          onSave={handleReceiptSaved}
        />
      )}

      {showSuccessMessage && (
        <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5" style={{ color: 'var(--success, #16a34a)' }} />
            <span style={{ color: 'var(--text)' }}>Receipt saved successfully!</span>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Goods Receipts</h1>
          <p className="text-sm" style={{ color: 'var(--muted)' }}>Track and manage received goods against purchase orders</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex space-x-3">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'var(--muted)' }} />
              <input
                type="text"
                placeholder="Search receipts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 rounded-lg border"
                style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
              />
            </div>
            <select
              value={expenseType}
              onChange={(e) => setExpenseType(e.target.value)}
              className="rounded-lg px-4 py-2 border"
              style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
            >
              <option value="all">All Types</option>
              <option value="IT">IT Expenses</option>
              <option value="Admin">Admin Expenses</option>
              <option value="Accounts">Accounts Expenses</option>
            </select>
            <button
              onClick={() => fetchGoodsReceipts()}
              disabled={loading}
              className="px-4 py-2 rounded-lg border font-medium hover:opacity-80 disabled:opacity-50 flex items-center gap-2"
              style={{ background: 'var(--panel)', color: 'var(--text)', borderColor: 'var(--border)' }}
              title="Refresh data"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl shadow-sm p-6 border" style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Receipts Overview</h2>
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              View and manage all goods receipts in the system
              {loading && !isInitialLoad && (
                <span className="ml-2">• Loading more data... {loadingProgress}%</span>
              )}
              {lastRefresh && !loading && (
                <span className="ml-2">• Last updated: {lastRefresh.toLocaleTimeString()}</span>
              )}
            </p>
          </div>
        </div>
        
        {/* Mobile Card View */}
        <div className="block lg:hidden space-y-4">
          {sortedAndPaginatedRows.length > 0 ? (
            sortedAndPaginatedRows.map((row) => (
              <div key={row.id} className="border rounded-lg p-4 space-y-3" style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-lg" style={{ color: 'var(--text)' }}>{row.expense_id}</h3>
                    <p className="text-sm" style={{ color: 'var(--muted)' }}>{row.expense_name}</p>
                  </div>
                  <div className="flex flex-col space-y-1">
                    {row.receipt_files && Array.isArray(row.receipt_files) && row.receipt_files.length > 0 ? (
                      row.receipt_files.filter(f => f).slice(0, 2).map((file, idx) => (
                        <a
                          key={idx}
                          href={file.startsWith('http') ? file : `/${file}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium"
                          style={{ background: 'var(--primary)', color: '#fff' }}
                        >
                          <DocumentIcon className="h-3 w-3 mr-1" />
                          View
                        </a>
                      ))
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-md" style={{ background: 'rgba(148,163,184,0.16)', color: 'var(--muted)' }}>No Receipt</span>
                    )}
                    {row.receipt_files && row.receipt_files.filter(f => f).length > 2 && (
                      <span className="text-xs" style={{ color: 'var(--muted)' }}>
                        +{row.receipt_files.filter(f => f).length - 2} more
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium" style={{ color: 'var(--muted)' }}>Ordered:</span>
                    <div style={{ color: 'var(--text)' }}>
                      {Array.isArray(row.ordered_quantities) 
                        ? row.ordered_quantities.reduce((sum, qty) => sum + (parseFloat(qty) || 0), 0)
                        : row.ordered_quantities || '—'
                      }
                    </div>
                  </div>
                  <div>
                    <span className="font-medium" style={{ color: 'var(--muted)' }}>Received:</span>
                    <div style={{ color: 'var(--text)' }}>
                      {Array.isArray(row.quantities) 
                        ? row.quantities.reduce((sum, qty) => sum + (parseFloat(qty) || 0), 0)
                        : row.quantities || '—'
                      }
                    </div>
                  </div>
                </div>
                
                {row.notes_list && (
                  <div className="text-sm">
                    <span className="font-medium" style={{ color: 'var(--muted)' }}>Notes:</span>
                    <div style={{ color: 'var(--text)' }} className="mt-1">
                      {Array.isArray(row.notes_list) ? row.notes_list.join(', ') : row.notes_list}
                    </div>
                  </div>
                )}
                
                <div className="flex justify-end pt-2">
                  {!row.receipts_closed && (
                    <button
                      onClick={() => handleAddReceipt(row)}
                      className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center space-x-2"
                      style={{ background: '#8c211e', color: '#fff' }}
                    >
                      <DocumentPlusIcon className="h-4 w-4" />
                      <span>Add Receipt</span>
                    </button>
                  )}
                  {row.receipts_closed && (
                    <span className="px-4 py-2 rounded-lg text-sm font-medium" style={{ background: 'rgba(148,163,184,0.16)', color: 'var(--muted)' }}>
                      Receipts Closed
                    </span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-10" style={{ color: 'var(--muted)' }}>
              {loading ? 'Loading...' : 'No goods receipts found'}
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full" style={{ color: 'var(--text)' }}>
            <thead className="border-b" style={{ borderColor: 'var(--border)', background: 'var(--panel)', color: 'var(--muted)' }}>
              <tr>
                {columns.map((column, idx) => (
                  <th key={idx} className={`px-4 py-3 text-left text-md font-medium tracking-wider cursor-pointer ${column.className}`} onClick={() => requestSort(column.key)}>
                    <div className="flex items-center gap-1">{column.label} {renderSortIcon(column.key)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedAndPaginatedRows.length > 0 ? (
                sortedAndPaginatedRows.map((row) => (
                  <tr key={row.id} className="hover:bg-opacity-50" style={{ background: 'var(--panel)' }}>
                    {columns.map((column, idx) => (
                      <td key={idx} className={`px-4 py-3 whitespace-nowrap ${column.className}`} style={{ color: 'var(--text)' }}>
                        {updatingExpenseId === row.expense_id && column.key === 'quantities' ? (
                          <div className="flex items-center justify-center">
                            <ArrowPathIcon className="h-4 w-4 animate-spin" style={{ color: 'var(--primary)' }} />
                          </div>
                        ) : (
                          renderCell(row, column)
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-10 text-center" style={{ color: 'var(--muted)' }}>
                    {loading ? 'Loading...' : 'No goods receipts found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
          
        {/* Pagination */}
        {filteredRows.length > 0 && (
          <div className="border-t" style={{ borderColor: 'var(--border)' }}>
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsPerPage={pagination.itemsPerPage}
              onItemsPerPageChange={(n) => setPagination(prev => ({ ...prev, itemsPerPage: n, currentPage: 1 }))}
              totalItems={pagination.totalItems}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GoodsReceiptsPage;
